/**
 * Modal for creating a team / project scope group.
 *
 * Name + slug + optional description. The slug is immutable after create
 * (it becomes the shared-LTM mirror directory name) — server-side validation
 * errors (bad slug format 400, duplicate slug 409) surface as a warn toast.
 */
import { useState } from "react";

import { adminApi, type AdminScopeGroupType } from "../../api/admin";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/toast/context";

interface Props {
  open: boolean;
  typeName: AdminScopeGroupType;
  onClose: () => void;
  /** Called with the new group's id after a successful create. */
  onCreated: (groupId: string) => void;
}

export function ScopeGroupCreateModal({
  open,
  typeName,
  onClose,
  onCreated,
}: Props) {
  const createMutation = adminApi.scopes.createGroup(typeName);
  const toast = useToast();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");

  const label = typeName === "team" ? "team" : "project";

  const reset = () => {
    setName("");
    setSlug("");
    setDescription("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedSlug = slug.trim();
    if (!trimmedName || !trimmedSlug) {
      toast.error("Name and slug are required");
      return;
    }
    createMutation.mutate(
      {
        name: trimmedName,
        slug: trimmedSlug,
        description: description.trim() || null,
      },
      {
        onSuccess: (group) => {
          reset();
          onCreated(group.id);
        },
        onError: (e) => toast.error(e),
      },
    );
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title={typeName === "team" ? "New team" : "New project"}
        eyebrow="Admin"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label" htmlFor="scope-group-name">
              Name
            </label>
            <input
              id="scope-group-name"
              className="input"
              placeholder={typeName === "team" ? "CRM Team" : "Pilot 2026"}
              required
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="scope-group-slug">
              Slug
            </label>
            <input
              id="scope-group-slug"
              className="input"
              placeholder={typeName === "team" ? "crm" : "pilot-2026"}
              required
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
            />
            <p className="text-xs text-ink-low mt-1">
              Lowercase letters, digits, "-" and "_" only. Immutable after
              create — it becomes the shared-LTM mirror directory name.
            </p>
          </div>
          <div>
            <label className="label" htmlFor="scope-group-description">
              Description
            </label>
            <textarea
              id="scope-group-description"
              className="input"
              rows={2}
              placeholder="(optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ resize: "vertical" }}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-rule">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-main"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? "Creating…" : `Create ${label}`}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
